import { merge } from 'lodash'
import { useEffect, useMemo } from 'react'
import { FieldErrors, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useIsINSEnabled } from '@/shared/model/useIsINSEnabled'
import { hasValidInsControlKey } from '@/shared/lib/ins-search'
import { useIsUS } from '@/hooks/useIsUS'
import { useIsSA } from '@/hooks/useIsSA'
import { applyBirthIdentityDefaults, isInsBirthGivenPrefixAllowed } from '../lib/identity-naming'

function usePatientProfileFormSchema(ssnLength: number, isINSEnabled: boolean) {
  const isUS = useIsUS()
  const isSA = useIsSA()
  const { i18n } = useLingui()

  return useMemo(
    () =>
      z
        .object({
          identityConfirmed: z.boolean(),
          agreement: isSA ? z.string() : z.string().min(1),
          ignoreSecu: z.boolean().optional(),
          secu: isUS || isSA ? z.string().optional() : z.string(),
          insNIR: z.string(),
          insOID: z.string().optional(),
          insEmitter: z.string().optional(),
          insHistory: z
            .array(
              z.object({
                value: z.string().optional(),
                key: z.string().optional(),
                startDate: z.string().optional(),
                endDate: z.string().optional(),
                oid: z.string().optional(),
              })
            )
            .optional(),
          name: isINSEnabled ? z.string().optional() : z.string().min(2, t`This field is required`),
          birthName: z.string().optional(),
          birthFirstName: isINSEnabled ? z.string().min(1, t`This field is required`) : z.string().optional(),
          surname: isINSEnabled ? z.string().optional() : z.string().min(2, t`This field is required`),
          birthSurname: isINSEnabled ? z.string().min(1, t`This field is required`) : z.string().optional(),
          email: z.string().email().optional().or(z.literal('')),
          birthday:
            isINSEnabled || isSA
              ? z
                  .date()
                  .max(new Date())
                  .nullable()
                  .refine((value) => value !== null, { message: isSA ? t`Date of birth is required` : t`This field is required` })
              : z.date().max(new Date()).nullable().optional(),
          birthPlaceCode: isINSEnabled ? z.string().min(1, t`This field is required`) : z.string().optional(),
          identityJustificationType: z.string().optional(),
          identitySupplementaryAttribute: z.string().optional(),
          gender:
            isINSEnabled || isSA
              ? z.enum(['M', 'F', 'I', '']).refine((val) => val === 'M' || val === 'F', { message: t`Gender must be Male or Female` })
              : z.enum(['M', 'F', 'I', '']).optional(),
          phone: z.string(),
          nationality: z.string().optional(),
          caisse: z.string(),
          codeRegime: z.string(),
          codeGestion: z.string(),
          street: z.string(),
          city: z.string(),
          cp: z.string(),
          country: z.string(),
          weight: z
            .string()
            .optional()
            .transform((value) => (value === '' ? undefined : value))
            .pipe(z.string().min(2).max(3).optional()),
          height: z
            .string()
            .optional()
            .transform((value) => (value === '' ? undefined : value))
            .pipe(z.string().min(3).max(3).optional()),
          allergy: z.string(),
          vaccins: z.string(),
          notes: z.string(),
          background: z.string(),
          treatment: z.string(),
          parents: z
            .array(
              z.object({
                firstname: z.string().min(1),
                lastname: z.string().min(1),
                phone: z.string().optional(),
                email: z.string().email().optional().or(z.literal('')),
                city: z.string().optional(),
                cp: z.string().optional(),
              })
            )
            .min(isSA ? 1 : 0, 'At least one emergency contact is required'),
          privateDoctor: z
            .object({
              firstname: z.string().min(1),
              lastname: z.string().min(1),
              phone: z.string().optional(),
              email: z.string().email().optional().or(z.literal('')),
              city: z.string().optional(),
              cp: z.string().optional(),
            })
            .optional(),
          pluginId: z.string().optional(),
          insured: z.string().optional(),
          insuredRelationship: z.string().optional(),
          insuranceProvider: z.string().optional(),
          primaryInsurance: z.string().optional(),
          secondaryInsurance: z.string().optional(),
          primaryPolicy: z.string().optional(),
          secondaryPolicy: z.string().optional(),
          primaryPharmacy: z.string().optional(),
        })
        .superRefine((data, ctx) => {
          if (!isUS && !isSA && !data.ignoreSecu && data.secu !== undefined && data.secu.length !== ssnLength) {
            ctx.addIssue({
              code: 'custom',
              message: t`Social security number must be exactly ${ssnLength} characters`,
              path: ['secu'],
            })
          }
          if (isINSEnabled) {
            // Mirrors INS_REQUIRED_USER_INFO_FIELDS enforced by the apiGateway PatientValidatorService
            if (!data.birthSurname?.trim()) {
              ctx.addIssue({ code: 'custom', message: 'This field is required', path: ['birthSurname'] })
            }
            if (!data.birthName?.trim()) {
              ctx.addIssue({ code: 'custom', message: 'This field is required', path: ['birthName'] })
            }
            if (!data.birthday) {
              ctx.addIssue({ code: 'custom', message: 'This field is required', path: ['birthday'] })
            }
            if (!data.gender) {
              ctx.addIssue({ code: 'custom', message: 'This field is required', path: ['gender'] })
            }
          }
          if (data.insNIR && data.insNIR.length !== 15) {
            ctx.addIssue({
              code: 'custom',
              message: t`The INS registration number, including its key, must be exactly 15 characters`,
              path: ['insNIR'],
            })
          }
          if (data.insNIR?.length === 15 && !hasValidInsControlKey(data.insNIR)) {
            ctx.addIssue({
              code: 'custom',
              message: t`The INS control key is invalid.`,
              path: ['insNIR'],
            })
          }
          if (isINSEnabled && data.birthName && data.birthFirstName && !isInsBirthGivenPrefixAllowed(data.birthName, data.birthFirstName)) {
            ctx.addIssue({
              code: 'custom',
              message: t`The first birth given name must match the beginning of the birth given names.`,
              path: ['birthFirstName'],
            })
          }
        }),
    [isINSEnabled, isUS, isSA, ssnLength]
  )
}

export type PatientProfileFormData = z.infer<ReturnType<typeof usePatientProfileFormSchema>>

export type UsePatientProfileFormProps = {
  initialValues?: Partial<PatientProfileFormData>
  errors?: FieldErrors<PatientProfileFormData>
}

const defaultFormErrors: FieldErrors<PatientProfileFormData> = {}

export function usePatientProfileForm({ initialValues: initialValuesProp, errors = defaultFormErrors }: UsePatientProfileFormProps) {
  const ins = useIsINSEnabled()
  const isSA = useIsSA()
  const authUser = useAppSelector(selectAuthUser)
  const ssnLength = authUser?.userInfo.language === 'it' ? 16 : 15

  const initialValues: PatientProfileFormData = useMemo(() => {
    const merged = merge(
      {
        identityConfirmed: false,
        agreement: '',
        name: '',
        birthName: '',
        birthFirstName: '',
        surname: '',
        birthSurname: '',
        ignoreSecu: false,
        secu: '',
        email: '',
        // SEHA only offers Male/Female, so do not preselect the Unknown gender there.
        gender: isSA ? '' : 'I',
        nationality: '',
        phone: '',
        birthday: null,
        birthPlaceCode: '',
        caisse: '',
        codeRegime: '',
        codeGestion: '',
        street: '',
        city: '',
        cp: '',
        country: '',
        weight: '',
        height: '',
        allergy: '',
        vaccins: '',
        notes: '',
        background: '',
        treatment: '',
        parents: [],
        insNIR: '',
        insOID: '',
        insEmitter: '',
        identityJustificationType: '',
        identitySupplementaryAttribute: '',
        insured: '',
        insuredRelationship: '',
        insuranceProvider: '',
        primaryInsurance: '',
        secondaryInsurance: '',
        primaryPolicy: '',
        secondaryPolicy: '',
        primaryPharmacy: '',
      },
      initialValuesProp
    )

    return applyBirthIdentityDefaults(merged, ins.isINSEnabled)
  }, [initialValuesProp, isSA, ins.isINSEnabled])

  const schema = usePatientProfileFormSchema(ssnLength, ins.isINSEnabled)
  const rhForm = useForm<PatientProfileFormData>({ defaultValues: initialValues, errors, resolver: zodResolver(schema) })
  const { reset } = rhForm

  useEffect(() => {
    reset(initialValues, { keepErrors: true })
  }, [initialValues, reset])

  return {
    isINSEnabled: ins.isINSEnabled,
    rhForm,
    ssnLength,
  }
}
